<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="700">
        <v-form class="mb-3" validate-on="input lazy" @submit.prevent="updateGateway">
            <v-card>
                <v-card-title id="heading" class="d-flex justify-space-between">
                    <h4>Update Gateway</h4>
                    <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue',false)"/>
                </v-card-title>

                <v-container>
                    <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                             density="compact" variant="tonal">
                        <p v-for="error in errors">{{ error }}</p>
                    </v-alert>
                    <v-row>
                        <v-col v-if="authStore.user.type==='admin'" class="py-0" cols="12" sm="6">
                            <company-select v-model="formData['company_id']" :items="companies" :rules="[vRules.required]"
                                      density="compact" label="Company">
                            </company-select>
                        </v-col>
                        <v-col class="py-0" cols="12" sm="6">
                            <v-select v-model="formData['type']" :items="gatewayTypes" :rules="[vRules.required]"
                                      density="compact" label="Type">
                            </v-select>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field :disabled="!isAdmin" v-model="formData['serial_number']" autofocus
                                          :rules="[vRules.required]" density="compact" label="Serial Number"/>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field :disabled="!isAdmin" v-model="formData['eth_mac']"
                                          :rules="[vRules.required]" density="compact" label="ETH Mac"/>
                        </v-col>

                        <v-col cols="12" sm="6" v-if="authStore.user.type ==='admin'">
                            <v-text-field v-model="formData['wifi_mac']"
                                          :disabled="!isAdmin" :rules="[vRules.required]" density="compact"
                                          label="Wifi Mac"/>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['cellular_imei']" :disabled="!isAdmin"
                                          density="compact" label="Cellular Imei"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['cellular_sim1_iccid']" :disabled="!isAdmin"
                                          density="compact" label="Cellular Sim1 Iccid"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['cellular_sim2_iccid2']" :disabled="!isAdmin"
                                          density="compact" label="Cellular Sim2 Iccid"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field :disabled="!isAdmin" v-model="formData['ble_mac1']" :rules="[vRules.required]"
                                          density="compact" label="Ble mac1"/>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field :disabled="!isAdmin" v-model="formData['ble_mac2']" :rules="[vRules.required]"
                                          density="compact" label="Ble mac2"/>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field :disabled="!isAdmin" v-model="formData['ble_mac3']" :rules="[vRules.required]"
                                          density="compact" label="Ble mac3"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['location_label']" density="compact"
                                          label="Location label"/>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['address_1']" density="compact" label="Address 1"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['address_2']" density="compact" label="Address 2"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['city']" density="compact" label="City"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['state']" density="compact" label="State"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['country_code']" density="compact" label="Country Code"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['zip_postal_code']" density="compact"
                                          label="Zip Postal Code"/>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" style="height: 300px">
                            <l-map ref="mapContainer"
                                   :center="[(formData.polygon_coordinates?.[0]?.lat??47.41322), (formData.polygon_coordinates?.[0]?.lng??-1.219482)]"
                                   :zoom="10" @ready="mapIsReady">
                                <l-tile-layer :url="$mapTiles.street"/>
                            </l-map>
                        </v-col>
                    </v-row>
                </v-container>

                <v-card-actions class="px-4 d-flex justify-end">
                    <v-btn size="small" type="button"
                           @click="$emit('update:modelValue',false)">
                        Close
                    </v-btn>
                    <v-btn color="primary" size="small" type="submit">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>
</template>

<script setup>
import "leaflet/dist/leaflet.css";
import "leaflet-geosearch/assets/css/leaflet.css";

import "leaflet-draw";
import L from "leaflet";
import {reactive, ref, onMounted, computed} from "vue";
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {LMap, LTileLayer} from "@vue-leaflet/vue-leaflet";
import {GeoSearchControl, OpenStreetMapProvider} from "leaflet-geosearch";
import {mapTiles} from "@/composables/mapConfig";
import CompanySelect from "@/components/globals/company-select.vue";

const mapContainer = ref();
const authStore = useAuthStore();
const snackbar = useSnackbarStore();
const props = defineProps({
    modelValue: Boolean,
    gateway: {
        type: Object
    }
});


const formData = reactive({
    valid: false,
    company_id: props.gateway.company_id ?? null,
    type: props.gateway.type ?? null,
    serial_number: props.gateway.serial_number ?? null,
    eth_mac: props.gateway.eth_mac ?? null,
    wifi_mac: props.gateway.wifi_mac ?? null,
    cellular_imei: props.gateway.cellular_imei ?? null,
    cellular_sim1_iccid: props.gateway.cellular_sim1_iccid ?? null,
    cellular_sim2_iccid2: props.gateway.cellular_sim2_iccid2 ?? null,
    ble_mac1: props.gateway.ble_mac1 ?? null,
    ble_mac2: props.gateway.ble_mac2 ?? null,
    ble_mac3: props.gateway.ble_mac3 ?? null,
    location_label: props.gateway.location_label ?? null,
    address_1: props.gateway.address_1 ?? null,
    address_2: props.gateway.address_2 ?? null,
    city: props.gateway.city ?? null,
    state: props.gateway.state ?? null,
    country_code: props.gateway.country_code ?? null,
    zip_postal_code: props.gateway.zip_postal_code ?? null,
    polygon_coordinates: props.gateway?.polygon_coordinates?.map((coordinate) => {
        return {'lat': coordinate.latitude, 'lng': coordinate.longitude};
    }) ?? [],
});

const emits = defineEmits(['update:modelValue', 'updateGateway']);
const dialogSwitch = ref(props.modelValue);
const companies = ref([]);
const errors = ref([]);
const gatewayTypes = [
    {value: 1, title: "LORA"},
    {value: 2, title: "BLE to WIFI"},
    {value: 3, title: "BLE to Cellular"},
    {value: 4, title: "WIFI to Cellular"},
];

onMounted(() => {
    if (authStore.user.type === 'admin') {
        fetchCompaniesList();
    }
});

const fetchCompaniesList = () => {
    send('get', `companies/list`)
        .then((response) => {
            companies.value = response.data.data;
        })
        .catch((error) => {
            console.log(error)
            snackbar.showSnackbar('Companies List could not be loaded', 'error')
        })
}

const isAdmin = computed(() => {
    return authStore.user.type === 'admin';
});

function mapIsReady() {
    const map = mapContainer.value.leafletObject;
    map.attributionControl.remove();
    const geoSearchOptions = new GeoSearchControl({
        style: "bar",
        autoClose: true,
        keepResult: true,
        showMarker: false,
        searchLabel: "Enter address",
        provider: new OpenStreetMapProvider({searchUrl: mapTiles.searchUrl, reverseUrl: mapTiles.reverseUrl}),
    });

    map.addControl(geoSearchOptions);
    /*Add Draw Tool Bar Control*/
    const drawnItems = new L.FeatureGroup();
    map.addLayer(drawnItems);
    const drawControl = new L.Control.Draw({
        draw: {
            circle: false,
            polygon: {
                showArea: true,
                allowIntersection: false, // Restricts shapes to simple polygons
                drawError: {
                    color: '#9d1212', // Color the shape will turn when intersects
                    message: '<strong>Oh snap!<strong> you can\'t draw that!' // Message that will show when intersect
                }
            },
            // disable toolbar item by setting it to false
            marker: false,
            polyline: false,
            rectangle: false,
            circlemarker: false,
        },
        edit: {
            featureGroup: drawnItems
        }
    });
    map.addControl(drawControl);

    const polygon = L.polygon(formData.polygon_coordinates, {
        // editable: true,
    });
    drawnItems.addLayer(polygon);
    map.center = polygon.getBounds()['_northEast'];

    map.on('draw:created', function (e) {
        drawnItems.eachLayer(function (layer) {
            drawnItems.removeLayer(layer);
        });

        if (e.layerType === 'polygon') {
            formData['location_changed'] = 1;
            formData['polygon_coordinates'] = e.layer.getLatLngs()[0];
        }
        drawnItems.addLayer(e.layer);
    });

    map.on('draw:edited', function (e) {
        e.layers.eachLayer(function (layer) {
            if (layer instanceof L.Polygon) {
                formData['location_changed'] = 1;
                formData['polygon_coordinates'] = layer.getLatLngs()[0];
            }
        });
    });

    map.on('draw:deleted', function (e) {
        drawnItems.eachLayer(function (layer) {
            drawnItems.removeLayer(layer);
        });
        formData['location_changed'] = 1;
        formData['polygon_coordinates'] = null;
    });
}

const updateGateway = () => {
    errors.value = [];
    send('put', `gateways/${props.gateway.id}`, formData)
        .then((response) => {
            snackbar.showSnackbar(response.data.message);
            if (response.data.success) {
                emits('updateGateway', response.data.data);
                emits('update:modelValue', false);
            }
        })
        .catch(function (errorResponse) {
            console.error(errorResponse)
            if (errorResponse?.response?.data.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse?.response?.data.message) {
                errors.value.push(errorResponse.response.data.message);
            }
            snackbar.showSnackbar('Something went wrong', 'error');
            document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
        })
}

</script>
